import { Component, OnInit } from '@angular/core';
import { Validators, UntypedFormBuilder, UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { AppConstants } from 'src/app/app.constants';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ApiService } from 'src/app/api.service';
import { FunctionsService } from 'src/app/services/functions.service';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { DateAdapter } from '@angular/material/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OkHtmlModalComponent } from 'src/app/generic-components/ok-html-modal/ok-html-modal.component';

@Component({
  selector: 'app-edit-user',
  templateUrl: './edit-user.component.html',
  styleUrls: ['./edit-user.component.css'],
  providers: [NgxSpinnerService]
})
export class EditUserComponent implements OnInit {
  userId = this.route.snapshot.paramMap.get('id');
  _userToUpdate: any = {};
  editFormSubmitted = false;
  fieldOption = false;
  fieldGroup: any = {};
  dataSource: any;
  owner = false;
  currentDate = new Date(new Date().getFullYear() + '-' + (new Date().getMonth() + 1) + '-' + new Date().getDate());
  minDate = new Date((new Date().getFullYear() - 118) + '-01-01T00:00:00');
  dobDate: Date;
  lang = localStorage.getItem('language') || 'en';
  smsConsent = false;
  serverError = '';

  editUserForm = this.formBuilder.group({
    firstName: ['', [Validators.required, Validators.maxLength(50), Validators.pattern(AppConstants.nameRegEx)]],
    lastName: ['', [Validators.required, Validators.maxLength(50), Validators.pattern(AppConstants.nameRegEx)]],
    phoneNumber: ['', AppConstants.phoneRegExReq],
    phoneNumberResidence: ['', AppConstants.phoneRegEx],
    userEmail: ['', [Validators.required, Validators.maxLength(320), Validators.pattern(AppConstants.emailRegex)]],
    userRole: ['', Validators.required],
    sendSms: [false],
    dob: [''],
    gender: [''],
    city: ['', [Validators.maxLength(60), Validators.pattern(AppConstants.addressRegex)]],
    primaryContactName: ['', [Validators.maxLength(50), Validators.pattern(AppConstants.nameRegEx)]],
    primaryContactPhoneNumber: ['', AppConstants.phoneRegEx],
    secondaryContactName: ['', [Validators.maxLength(50), Validators.pattern(AppConstants.nameRegEx)]],
    secondaryContactPhoneNumber: ['', AppConstants.phoneRegEx],
    state: ['', [Validators.maxLength(60), Validators.pattern(AppConstants.addressRegex)]],
    address: ['', [Validators.maxLength(150), Validators.pattern(AppConstants.addressRegex)]],
    country: ['', [Validators.maxLength(60), Validators.pattern(AppConstants.addressRegex)]],
    zip: new UntypedFormControl('', [Validators.maxLength(5), Validators.pattern(AppConstants.numberRegex)])
  });

  constructor(
    private formBuilder: UntypedFormBuilder,
    private modalService: NgbModal,
    public functionsService: FunctionsService,
    private apiService: ApiService,
    public translate: TranslateService,
    private spinner: NgxSpinnerService,
    private route: ActivatedRoute,
    private router: Router,
    private dateAdapter: DateAdapter<Date>
  ) {
    this.getUserFieldOption();
  }

  ngOnInit() {
    if (this.userId) {
      this.editUser(this.userId);
    } else {
      this.router.navigateByUrl('/main/users');
    }
    this.dateAdapter.setLocale(this.lang);
    this.translate.onLangChange.subscribe((event) => {
      this.lang = event.lang;
      this.dateAdapter.setLocale(this.lang);
    });
    if (localStorage.getItem('isOwner') != null && localStorage.getItem('isOwner') == '1') {
      this.owner = true;
    }
  }

  editUserSave() {
    this.serverError = '';
    this.editUserForm.markAllAsTouched();
    if (!this.editUserForm.valid) {
      this.editFormSubmitted = true;
      return;
    }
    const formValue = this.editUserForm.getRawValue();
    Object.keys(formValue).forEach((key) => {
      if (key !== 'dob') {
        const val = formValue[key];
        formValue[key] = (typeof val === 'boolean') ? val : (val || '');
      }
    });
    const bd = formValue['dob'] ? this.functionsService.formatDate({ value: formValue['dob'] }) : null;
    formValue['dob'] = bd;
    formValue['sendSms'] = this.smsConsent === true;
    this.spinner.show();
    Object.assign(this._userToUpdate, formValue);
    if (this._userToUpdate.user) {
      this._userToUpdate.user.sendSms = this.smsConsent === true;
    }
    this.apiService.editUserProfile(this._userToUpdate).subscribe(
      (result) => {
        if (result['content'] !== 'Updated') {
          let fields = '';
          const list = result['content'].toString().split(',');
          list.forEach(element => {
            const name = this.dataSource.find(o => o.id == element);
            fields += '<li>' + name.field + '</li><br>';
          });
          const modalRef = this.modalService.open(OkHtmlModalComponent, { size: 'lg', windowClass: 'modal' });
          modalRef.componentInstance.title = this.translate.instant('updated_user');
          modalRef.componentInstance.message = this.translate.instant('fields_changed_update') + '<br><ol>' + fields + '</ol>';
          modalRef.result.then(() => modalRef.close());
        }
        this.spinner.hide();
        this.router.navigateByUrl('/main/users');
      },
      error => {
        this.spinner.hide();
        if (error['error']['content'] != null) {
          this.serverError = this.translate.instant(error['error']['content']);
          this.translate.onLangChange.subscribe(() => {
            if (this.serverError) {
              this.serverError = this.translate.instant(error['error']['content']);
            }
          });
        } else {
          this.functionsService.errorHandler(error['status']);
        }
      }
    );
  }

  clearDobDate() {
    this.dobDate = null;
  }

  private fetchCustomFields() {
    this.apiService.getCustomFields().subscribe(resp => {
      const jsonObject = resp as any[];
      if (jsonObject != null) {
        jsonObject.forEach(field => {
          this.fieldGroup[field['id'].toString()] = new UntypedFormControl(this._userToUpdate['custom'][field['id'].toString()]);
        });
        this.editUserForm.addControl('custom', new UntypedFormGroup(this.fieldGroup));
        this.dataSource = jsonObject;
      }
      this.spinner.hide();
    }, error => {
      this.spinner.hide();
      this.functionsService.errorHandler(error['status']);
    });
  }

  getUserFieldOption() {
    this.spinner.show();
    this.apiService.getUserFieldOption().subscribe(
      data => {
        this.fieldOption = data['userFieldOption'] === 'YES';
        this.spinner.hide();
      },
      error => {
        this.fieldOption = false;
        this.spinner.hide();
      }
    );
  }

  editUser(userId) {
    this.spinner.show();
    this.apiService.getUserProfile(userId).subscribe(
      data => {
        if (this.fieldOption) {
          this.fetchCustomFields();
        }
        this._userToUpdate = data;
        this._userToUpdate['dob'] = this._userToUpdate['dob'] ? new Date(this._userToUpdate['dob'] + 'T12:00:00Z') : '';
        this.dobDate = this._userToUpdate['dob'];
        this.editUserForm.patchValue(this._userToUpdate);
        // Apply sendSms value from nested user object
        const sendSmsValue = this._userToUpdate.user?.sendSms === true
          || this._userToUpdate.user?.sendSms === 'true'
          || this._userToUpdate.user?.sendSms === 1;
        this.editUserForm.patchValue({ sendSms: sendSmsValue });
        this.smsConsent = sendSmsValue;
        this.editUserForm.get('sendSms').disable();
        this.spinner.hide();
      },
      error => {
        this.spinner.hide();
        this.functionsService.errorHandler(error['status']);
      }
    );
  }

  isFieldInvalid(fieldName: string): boolean {
    const control = this.editUserForm.get(fieldName);
    return control && control.invalid && (this.editFormSubmitted || control.touched || control.dirty);
  }

  isFieldValid(fieldName: string): boolean {
    const control = this.editUserForm.get(fieldName);
    return control && control.valid && (control.touched || control.dirty);
  }

  hasError(fieldName: string, errorType: string): boolean {
    const control = this.editUserForm.get(fieldName);
    return control && control.hasError(errorType);
  }
}
